import {
  AddCustomKeyBody,
  CustomKeyAttributes,
  CustomKeyMode,
  CustomKeyTypesEnum,
} from '@loyalty/shared-common/profile-api-client';
import { Alert } from '@mui/material';
import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useAlerts } from '@qualifio/ui-commons';
import { useTranslation } from 'react-i18next';
import { uuid } from '@loyalty/shared-common';
import { IsLoading, useLoaders } from '../shared/context/context-utils';
import { useAccounts } from '../accounts/AccountsContext';
import { useApi } from '../shared/api-client/ApiClientProvider';
import { useSelectiveEffect } from '../shared/use-selective-effect';

export interface ICustomKeysContext {
  customKeys: CustomKeyAttributes[];
  customKeysById: { [id: string]: CustomKeyAttributes };
  selectedCustomKey: CustomKeyAttributes | undefined;
  createCustomKey: (model?: CustomKeyAttributes) => void;
  addCustomKey: (customKey: AddCustomKeyBody) => void;
  updateCustomKey: (customKeyId: string, customKey: AddCustomKeyBody) => void;
  deleteCustomKey: (customKeyId: string) => Promise<void>;
  getCustomKeys: () => void;
  setSelectedCustomKey: (customKey: CustomKeyAttributes | undefined) => void;
  isLoading: IsLoading;
}

export const CustomKeysContext = createContext<ICustomKeysContext>({} as any);

export function useCustomKeys() {
  return useContext(CustomKeysContext);
}

export function CustomKeysProvider({ children }: { children: ReactNode }) {
  const { currentAccount } = useAccounts();
  const { t } = useTranslation();
  const { notify } = useAlerts();
  const [customKeys, setCustomKeys] = useState<CustomKeyAttributes[]>([]);
  const { profileApi, managerApi } = useApi();
  const [error, setError] = useState<any>();
  const [selectedCustomKey, setSelectedCustomKey] = useState<CustomKeyAttributes>();

  const { addLoader, removeLoader, isLoading } = useLoaders();

  const createCustomKey = useCallback(
    (model?: CustomKeyAttributes) => {
      const customKey = {
        ...(model || {
          technicalName: '',
          type: CustomKeyTypesEnum.STRING,
          mode: CustomKeyMode.IMPORT,
        }),
        name: model?.name ? `${model?.name} ${t('generic.duplicated-suffix')}` : '',
        id: uuid(),
        isNew: true,
      } as CustomKeyAttributes;

      setCustomKeys(prev => [customKey, ...prev]);
      setSelectedCustomKey(customKey);
    },
    [t],
  );

  const addCustomKey = useCallback(
    (customKey: AddCustomKeyBody) => {
      addLoader('addCustomKey');

      profileApi
        .addCustomKey(currentAccount.id, customKey)
        .then(newCustomKey => {
          setCustomKeys(prev => prev.map(key => (key.id === customKey.id ? newCustomKey : key)));
          setSelectedCustomKey(newCustomKey);
          notify(t('shared.saved'), 'success');
        })
        .catch(setError)
        .finally(() => removeLoader('addCustomKey'));
    },
    [addLoader, currentAccount.id, notify, profileApi, removeLoader, t],
  );

  const updateCustomKey = useCallback(
    (customKeyId: string, customKey: AddCustomKeyBody) => {
      addLoader('updateCustomKey');

      profileApi
        .updateCustomKey(customKeyId, customKey)
        .then(updatedCustomKey => {
          setCustomKeys(prev =>
            prev.map(currency =>
              currency.id === updatedCustomKey.id ? updatedCustomKey : currency,
            ),
          );
          setSelectedCustomKey(updatedCustomKey);
          notify(t('shared.saved'), 'success');
        })
        .catch(setError)
        .finally(() => removeLoader('updateCustomKey'));
    },
    [addLoader, notify, profileApi, removeLoader, t],
  );

  const deleteCustomKey = useCallback(
    async (customKeyId: string) => {
      addLoader('deleteCustomKey');

      const customKey = customKeys.find(({ id }) => id === customKeyId);

      if ((customKey as any)?.['isNew']) {
        setCustomKeys(prev => {
          return prev.filter(customeKey => customeKey.id !== customKeyId);
        });
        if (selectedCustomKey?.id === customKey?.id) {
          setSelectedCustomKey(undefined);
        }
        removeLoader('deleteCustomKey');
      } else {
        await managerApi
          .deleteCustomKey(customKeyId)
          .then(() => {
            setCustomKeys(prev => {
              return prev.filter(customKey => customKey.id !== customKeyId);
            });
            setSelectedCustomKey(prev => (prev?.id === customKey?.id ? undefined : prev));

            notify(t('shared.actions.saved'), 'success');
          })
          .catch(error => {
            notify(t('custom-keys.errors.delete', { error }), 'error');
          })
          .finally(() => removeLoader('deleteCustomKey'));
      }
    },
    [addLoader, currentAccount.id, customKeys, notify, profileApi, removeLoader, t],
  );

  const getCustomKeys = useCallback(() => {
    addLoader('getCustomKeys');

    profileApi
      .getAccountCustomKeys(currentAccount.id)
      .then(customKeys => {
        setCustomKeys(customKeys);
      })
      .catch(setError)
      .finally(() => removeLoader('getCustomKeys'));
  }, [addLoader, currentAccount.id, profileApi, removeLoader]);

  const customKeysById = useMemo(
    () => Object.fromEntries(customKeys.map(customKey => [customKey.id, customKey])),
    [customKeys],
  );

  useSelectiveEffect(
    () => {
      getCustomKeys();
    },
    [getCustomKeys, currentAccount.id],
    [currentAccount.id],
  );

  const context = {
    customKeys,
    customKeysById,
    selectedCustomKey,
    createCustomKey,
    addCustomKey,
    updateCustomKey,
    deleteCustomKey,
    getCustomKeys,
    setSelectedCustomKey,
    isLoading,
  };

  return error ? (
    <Alert severity="error">{error.toString()}</Alert>
  ) : (
    <CustomKeysContext.Provider value={context}>{children}</CustomKeysContext.Provider>
  );
}
